// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { formatRoundSetupDate } from '@/hooks/useRecentRoundGameSetups';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import type { GameDefinition } from '@/features/game-definitions/types';
import type { RecentRoundGameSetup } from '@/features/round-init/types';
import appColors, { alpha } from '@/theme/colors';

type CopyGamesFromRoundModalProps = {
  visible: boolean;
  setups: RecentRoundGameSetup[];
  gameDefs: GameDefinition[];
  onSelect: (setup: RecentRoundGameSetup) => void;
  onClose: () => void;
};

/**
 * Lists the player's recent rounds so their game selection can be copied into the round
 * being set up. Picking a round only prefills — everything stays editable afterwards.
 */
export default function CopyGamesFromRoundModal({
  visible,
  setups,
  gameDefs,
  onSelect,
  onClose,
}: CopyGamesFromRoundModalProps) {
  const { t } = useI18n();
  const fallbackName = t('selectGames.gameNameFallback');

  const gameLabel = (gameDefinitionId: string, weightPercent: number) => {
    const definition = gameDefs.find((candidate) => candidate.id === gameDefinitionId);
    const name = definition ? getGameDisplayName(definition, fallbackName) : fallbackName;
    return `${name} ${weightPercent} %`;
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>{t('selectGames.copyFromRoundTitle')}</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('selectGames.copyFromRoundClose')}
              onPress={onClose}
              style={styles.closeButton}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>
          <Text style={styles.subtitle}>{t('selectGames.copyFromRoundSubtitle')}</Text>

          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {setups.length === 0 ? (
              <Text style={styles.empty}>{t('selectGames.copyFromRoundEmpty')}</Text>
            ) : (
              setups.map((setup) => (
                <Pressable
                  key={setup.roundId}
                  accessibilityRole="button"
                  onPress={() => onSelect(setup)}
                  style={styles.row}
                >
                  <View style={styles.rowHeader}>
                    <Text style={styles.rowTitle} numberOfLines={1}>
                      {setup.name}
                    </Text>
                    <Text style={styles.rowDate}>{formatRoundSetupDate(setup)}</Text>
                  </View>
                  <View style={styles.chips}>
                    {setup.games.map((game) => (
                      <Text key={game.gameDefinitionId} style={styles.chip}>
                        {gameLabel(game.gameDefinitionId, game.weightPercent)}
                      </Text>
                    ))}
                  </View>
                </Pressable>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.ink, 0.72),
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '86%',
    alignSelf: 'center',
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    gap: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    gap: 12,
  },
  title: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  closeButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  closeButtonText: {
    fontSize: 16,
    color: appColors.text.secondary,
  },
  subtitle: {
    fontSize: 13,
    color: appColors.text.secondary,
  },
  scroll: {
    marginTop: 4,
  },
  scrollContent: {
    gap: 10,
    paddingBottom: 4,
  },
  empty: {
    fontSize: 14,
    color: appColors.text.secondary,
    textAlign: 'center',
    paddingVertical: 16,
  },
  row: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    padding: 12,
    gap: 8,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    gap: 8,
  },
  rowTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  rowDate: {
    fontSize: 12,
    color: appColors.text.secondary,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: 'transparent',
    gap: 6,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: appColors.surface.tintMid,
    borderWidth: 1,
    borderColor: appColors.accent.glow,
    color: appColors.text.accentSoft,
    fontSize: 12,
    fontWeight: '600',
    overflow: 'hidden',
  },
});
